import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import SubmitButton from '../components/SubmitButton';
import useShopifyApi from '../hooks/useShopifyApi';
import WarningCircle from '../assets/img/warning-circle.svg';
import CheckCircle from '../assets/img/check-circle.png';
import {
  addVwoDecision,
  addCheckoutAll,
  addPayFrequencyLookup,
  changeFeatureLinearFlow,
  addVerificationCheckoutUid,
} from '../redux/actions';

// eslint-disable-next-line no-shadow
function ExceededLimitLCR({ url }) {
  const { handleSubmit } = useForm();

  const [popupShown, setPopupShown] = useState(false);
  const [firstPopupShown, setFirstPopupShown] = useState(false);
  const [secondPopupShown, setSecondPopupShown] = useState(false);

  const store = useSelector(state => ({
    shopify: state.technical?.shopify,
    checkoutLookupData: state.personal?.checkoutLookupData,
    error: state.technical?.error,
    checkout: state.loanInfo?.checkout,
    phone: state.contact?.phone,
    newUserDetails: state.personal?.newUserDetails,
    verificationCode: state.loanInfo?.verificationCode,
  }));
  const { request, response, loading } = useShopifyApi();
  const dispatch = useDispatch();
  const history = useHistory();

  const goBackToRetailor = async () => {
    // Customer declined LCR. Reject the pending Shopify payment session for
    // both new-user and existing-user Shopify flows, then always redirect to
    // the Shopify cancel URL - even if the reject call fails.
    if (store.shopify?.isShopify) {
      try {
        const apiResponse = await request.rejectRequest({
          transaction_id: store.shopify?.transactionId,
          reject_reason: 'EXCEEDED_LIMIT',
        });
        await apiResponse?.json();
      } catch (e) {
        // Swallow: a failed reject must not block the cancel redirect.
      }
    }
    if (url) {
      window.location.href = url;
    }
  };

  const proceedWithLCR = async () => {
    const payload = {
      ...store.checkoutLookupData?.checkout_object,
      phone: store.phone,
      code: store.verificationCode,
    };
    await request.initializeRequest(payload);
    const data = await response.json();
    if (response.ok) {
      dispatch(addCheckoutAll(data));
      dispatch(
        addPayFrequencyLookup({
          first_name: store.checkoutLookupData?.checkout_object?.customer?.billing?.first_name,
        }),
      );
      dispatch(
        addVwoDecision({
          feature: 'offerPage',
          variation: 'Version-B',
        }),
      );
      dispatch(
        addVwoDecision({
          feature: 'checkoutPage',
          variation: 'Version-A',
        }),
      );
      dispatch(changeFeatureLinearFlow(false));
      dispatch(addVerificationCheckoutUid(data.uid));
      history.push('/display-offered-lease-details');
    } else {
      history.push('/error');
    }
  };

  return (
    <div className="k-content-wrapper">
      <section className="k-info k-lcr">
        <form onSubmit={handleSubmit(proceedWithLCR)} autoComplete="off">
          {store.shopify?.isNewUserFlow && (
            <>
              <div className="k-error-image-wrapper">
                <img width="150" height="150" className="congratulation-icon" src={CheckCircle} alt="" />
              </div>
              <h1 className="congratulation-header">Congratulations {store.newUserDetails?.billing_first_name}!</h1>
              <h3 className="congratulation-text">You are pre-approved to lease items with a cash price up to</h3>
              <h1 className="congratulation-amount">${store.newUserDetails?.available_limit}</h1>
            </>
          )}
          {!store.shopify?.isNewUserFlow && (
            <>
              <div className="k-error-image-wrapper">
                <img width="150" height="150" className="error-icon" src={WarningCircle} alt="" />
              </div>
              <h1 className="congratulation-header">Exceeded limit</h1>
              <p className="k-not-approved-text">{store.error?.message}</p>
            </>
          )}
          {!store.checkout?.is_valid && (
            <div className="k-lcr-explanation">
              <div>
                The cash price in your cart is <b>${store.checkout?.cart_total}</b> which exceeds your limit. You can:
              </div>
              <div className="k-lcr-line-with-tooltip">
                <span>1) Reduce your initial cart to ${store.checkout?.available_limit}</span>
                <span
                  className="k-lcr-tooltip-icon"
                  role="tooltip"
                  aria-hidden="true"
                  onClick={() => {
                    setFirstPopupShown(true);
                    setPopupShown(true);
                  }}
                >
                  i
                </span>
              </div>
              <div>or</div>
              <div className="k-lcr-line-with-tooltip">
                <span>2) Spend up to ${store.checkout?.allowed_limit} with a lease cost reduction</span>
                <span
                  className="k-lcr-tooltip-icon"
                  role="tooltip"
                  aria-hidden="true"
                  onClick={() => {
                    setSecondPopupShown(true);
                    setPopupShown(true);
                  }}
                >
                  i
                </span>
              </div>
            </div>
          )}
          {store.checkout?.is_valid && (
            <div className="k-lcr-explanation">
              <div>
                The cash price in your cart is <b>${store.checkout?.cart_total}</b> which exceeds your limit. You can:
              </div>
              <div className="k-lcr-line-with-tooltip">
                <span>1) Pay ${store.checkout?.downpayment_amount} to reduce the lease cost</span>
                <span
                  className="k-lcr-tooltip-icon"
                  role="tooltip"
                  aria-hidden="true"
                  onClick={() => {
                    setFirstPopupShown(true);
                    setPopupShown(true);
                  }}
                >
                  i
                </span>
              </div>
              <div>or</div>
              <div className="k-lcr-line-with-tooltip">
                <span>2) Reduce your initial cart by ${store.checkout?.downpayment_amount}</span>
                <span
                  className="k-lcr-tooltip-icon"
                  role="tooltip"
                  aria-hidden="true"
                  onClick={() => {
                    setSecondPopupShown(true);
                    setPopupShown(true);
                  }}
                >
                  i
                </span>
              </div>
            </div>
          )}
          {store.checkout?.is_valid && (
            <div className="k-submit k-error-button k-not-approved-button">
              <SubmitButton text="Make a lease cost reduction payment" loading={loading} />
              <button type="button" className="blue-button k-button-space" onClick={goBackToRetailor}>
                <strong>Reduce my cart</strong>
              </button>
              <div className="k-lcr-small-text">
                Don’t worry, you will be able to review your lease terms and cart on the next screen.
              </div>
            </div>
          )}
          {!store.checkout?.is_valid && (
            <div className="k-submit k-error-button k-not-approved-button">
              <button type="button" onClick={goBackToRetailor}>
                <strong>Edit my cart</strong>
              </button>
            </div>
          )}
        </form>
        <div className={`k-lcr-tooltip ${popupShown ? 'firstPopupShown' : 'firstPopupHidden'}`}>
          <div className="k-lcr-tooltip-header">
            {firstPopupShown && (
              <div className="k-lcr-main-text">
                {store.checkout?.is_valid && (
                  <>
                    <p>
                      Proceed with your cart as-is and at checkout make an optional payment of $
                      {store.checkout?.downpayment_amount} to reduce the lease cost. You will also need to pay the
                      initial payment of $45.00 + tax.
                    </p>
                    <p>
                      The cash price of the leased item(s) will be ${store.checkout?.available_limit} after you pay the
                      lease cost reduction of ${store.checkout?.downpayment_amount}.
                    </p>
                  </>
                )}
                {!store.checkout?.is_valid && (
                  <>
                    <p>
                      Modify your cart to reduce the total cash price of your item(s) to $
                      {store.checkout?.available_limit} or less and pay an initial payment of $45.00 + tax.
                    </p>
                    <p>
                      The cash price of the leased item(s) will be ${store.checkout?.available_limit} after you reduce
                      your cart by $
                      {parseFloat(store.checkout?.cart_total - store.checkout?.available_limit, 10).toFixed(2)}.
                    </p>
                  </>
                )}
              </div>
            )}
            {secondPopupShown && (
              <div className="k-lcr-main-text">
                {store.checkout?.is_valid && (
                  <>
                    <p>
                      Modify your initial cart to reduce the total cash price of your item(s) by $
                      {store.checkout?.downpayment_amount} or more. You will also need to pay the initial payment of
                      $45.00 + tax.
                    </p>
                    <p>
                      The cash price of the leased item(s) will be ${store.checkout?.available_limit} after you reduce
                      your cart by ${store.checkout?.downpayment_amount}.
                    </p>
                  </>
                )}
                {!store.checkout?.is_valid && (
                  <>
                    <p>
                      Modify your cart to reduce the total cash price of your item(s) to between $
                      {store.checkout?.available_limit} and ${store.checkout?.allowed_limit} and at checkout make an
                      optional $
                      {parseFloat(store.checkout?.allowed_limit - store.checkout?.available_limit, 10).toFixed(2)}{' '}
                      payment to reduce the lease cost, and initial payment of $45.00 plus tax.
                    </p>
                    <p>
                      The cash price of the leased item(s) will be ${store.checkout?.available_limit} after you pay the
                      lease cost reduction of $
                      {parseFloat(store.checkout?.allowed_limit - store.checkout?.available_limit, 10).toFixed(2)}.
                    </p>
                  </>
                )}
              </div>
            )}
            <div className="k-lcr-close-tooltip-header">
              <button
                aria-label="Close"
                type="button"
                className="k-lcr-close-tooltip"
                onClick={() => {
                  setPopupShown(false);
                  setFirstPopupShown(false);
                  setSecondPopupShown(false);
                }}
              />
            </div>
          </div>
          <div>
            {firstPopupShown && (
              <div className="k-lcr-tile-well">
                {store.checkout?.is_valid && (
                  <>
                    <p>
                      Initial cart <span className="k-lcr-make-right">${store.checkout?.cart_total}</span>
                    </p>
                    <p>
                      Lease cost reduction
                      <span className="k-lcr-make-right">-${store.checkout?.downpayment_amount}</span>
                    </p>
                    <hr />
                    <p>
                      After reductions <span className="k-lcr-make-right">${store.checkout?.available_limit}</span>
                    </p>
                  </>
                )}
                {!store.checkout?.is_valid && (
                  <>
                    <p>
                      Initial cart <span className="k-lcr-make-right">${store.checkout?.available_limit}</span>
                    </p>
                    <p>
                      Lease cost reduction <span className="k-lcr-make-right">-$0.00</span>
                    </p>
                    <hr />
                    <p>
                      After reductions <span className="k-lcr-make-right">${store.checkout?.available_limit}</span>
                    </p>
                  </>
                )}
              </div>
            )}
            {secondPopupShown && (
              <div className="k-lcr-tile-well">
                {store.checkout?.is_valid && (
                  <>
                    <p>
                      Initial cart <span className="k-lcr-make-right">${store.checkout?.available_limit}</span>
                    </p>
                    <p>
                      Lease cost reduction <span className="k-lcr-make-right">-$0.00</span>
                    </p>
                    <hr />
                    <p>
                      After reductions <span className="k-lcr-make-right">${store.checkout?.available_limit}</span>
                    </p>
                  </>
                )}
                {!store.checkout?.is_valid && (
                  <>
                    <p>
                      Initial cart <span className="k-lcr-make-right">${store.checkout?.allowed_limit}</span>
                    </p>
                    <p>
                      Lease cost reduction
                      <span className="k-lcr-make-right">
                        -${parseFloat(store.checkout?.allowed_limit - store.checkout?.available_limit, 10).toFixed(2)}
                      </span>
                    </p>
                    <hr />
                    <p>
                      After reductions <span className="k-lcr-make-right">${store.checkout?.available_limit}</span>
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default ExceededLimitLCR;
